import { Skel } from "../components/Skel";
import { SkelTable } from "../components/skeleton/SkelTable";

// DetalleTienda's loading compositions (ADR-0166). Every block sits in
// the real container of what replaces it — the same .page-header,
// .vgal, .panel, .maint__row, .timeline and .dtable boxes — so the data
// lands without a shift. Counts are what fills a 1280×800 viewport.

// The store carousel is always the hero variant (founder decision
// 2026-10-22): ~3 tiles of 320px fit at 1280px, so that's how many
// placeholders it draws. A hero tile and a hero observation card share
// the same box (.vgal__strip--hero .vgal__tile / .vgal__card).
const HERO_TILES = 3;

/** The photo strip while the latest acta's photos load. The tile box is
 *  the real hero .vgal__shot (fixed 320×200), filled edge to edge,
 *  plus the area-tag and caption lines in their own line boxes. */
export function VisitPhotosSkeleton() {
  return (
    <div className="vgal__strip vgal__strip--hero" aria-hidden="true">
      {Array.from({ length: HERO_TILES }, (_, i) => (
        <span key={i} className="vgal__tile">
          <span className="vgal__shot">
            <Skel variant="media" width="100%" height="100%" />
          </span>
          <span className="vgal__area-tag">
            <Skel variant="text" width="45%" />
          </span>
          <span className="vgal__cap">
            <Skel variant="text" width="80%" />
          </span>
        </span>
      ))}
    </div>
  );
}

/** The «Estado por sección» chips while GET /assets/:id/state loads. */
export function SectionStateSkeleton() {
  return (
    <div className="vsec" aria-hidden="true">
      <div className="vsec__title">Estado por sección</div>
      <div className="vsec__list">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="vsec__item">
            <span className="vsec__name">
              <Skel variant="text" width={72 + (i % 2) * 24} />
            </span>
            <span className="vsec__pct">
              <Skel variant="text" width={28} />
            </span>
            <span className="vsec__n">
              <Skel variant="text" width={20} />
            </span>
            <span className="vsec__bar" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Mantenciones rows (.maint__row), lead pair on the left and the due
 *  date + pill on the right, the same boxes the real rows use. */
export function MaintenanceRowsSkeleton() {
  return (
    <div aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="maint__row">
          <div className="maint__row-main">
            <div className="maint__lead skel-stack">
              <Skel width="55%" height={12} />
              <Skel width="40%" height={9} />
            </div>
            <div className="maint__due">
              <Skel width={64} height={10} />
              <Skel pill width={72} height={20} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** The page header frame while the dashboard aggregate loads: the
 *  eyebrow, title and chip lines are data, so each is a Skel inside the
 *  real line box. A div, not the <h1>: an h1 with no text is an empty
 *  heading to a screen reader; the status line already says what loads. */
export function DetalleTiendaHeaderSkeleton() {
  return (
    <div className="page-header">
      <div className="page-header__lead">
        <div className="page-header__eyebrow">
          <Skel variant="text" width="32%" />
        </div>
        <div className="page-header__title">
          <Skel variant="text" width="40%" />
        </div>
        <div className="detail-chip-row">
          <Skel pill width={88} height={22} />
          <Skel pill width={72} height={22} />
        </div>
      </div>
    </div>
  );
}

/** Everything under the header: the gallery + section strip, Mantenciones + Historial,
 *  and Observaciones abiertas. Panel titles are static copy, so they
 *  render for real. */
export function DetalleTiendaBodySkeleton({ reportNoun }: { reportNoun: string }) {
  return (
    <>
      <div className="vgal">
        <div className="vgal__bar">
          <div className="vgal__label">Estado del último {reportNoun}</div>
          <div className="vgal__meta mono">
            <Skel variant="text" width="22ch" />
          </div>
        </div>
        <VisitPhotosSkeleton />
        {/* A store with a latest acta (the usual case) shows its section
            strip under the photos; drawing it keeps the panels below
            from jumping when it lands. */}
        <SectionStateSkeleton />
      </div>

      <div className="detail" style={{ display: "block" }}>
        <div className="detail__main">
          <div className="grid-2" style={{ marginBottom: 20, alignItems: "start" }}>
            <div className="panel">
              <div className="panel__head">
                <div className="panel__title">Mantenciones</div>
                <div className="panel__sub mono">
                  <Skel variant="text" width="6ch" />
                </div>
              </div>
              <div className="panel__body--flush">
                <MaintenanceRowsSkeleton />
              </div>
            </div>

            <div className="panel">
              <div className="panel__head">
                <div className="panel__title">Historial de reportes</div>
                <div className="panel__sub mono">
                  <Skel variant="text" width="18ch" />
                </div>
              </div>
              <div className="panel__body" aria-hidden="true">
                <div className="timeline">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="timeline__item">
                      <div className="timeline__dot" />
                      <div className="timeline__row">
                        <div className="timeline__main skel-stack">
                          <Skel width="45%" height={10} />
                          <Skel width="60%" height={12} />
                        </div>
                        <Skel variant="btn" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="panel__head">
              <div className="panel__title">Observaciones abiertas</div>
              <div className="panel__sub mono">
                <Skel variant="text" width="8ch" />
              </div>
            </div>
            <SkelTable
              rows={4}
              cols={4}
              headers={["Observación", "Severidad", "Evidencia", "Estado"]}
            />
          </div>
        </div>
      </div>
    </>
  );
}
